import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import Sidebar from "../Dashboard/Sidebar";
import Topbar from "../Dashboard/Topbar";
import Loader from "../../components/Loader";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Lock } from "lucide-react";
import { getClinicsByUser } from "../../api/clinic";
import { getAllPatients } from "../../api/patient";
import {
  createOrUpdateTreatmentRecord,
  getTreatmentRecordById,
  TreatmentRecordPayload,
} from "../../api/treatmentRecord";
import { getTreatmentPlansByPatient, TreatmentPlan } from "../../api/treatmentPlan";
import { getPastAppointmentsByPatient } from "../../api/appointment";
import { getImageUrl } from "../../utils/imageUrl";

const TreatmentRecordForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);

  const [clinicsLoading, setClinicsLoading] = useState(false);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);

  const [clinics, setClinics] = useState<{ id: string; name: string }[]>([]);
  const [patients, setPatients] = useState<
    { id: string; name: string; clinicId: string; treatmentId?: string }[]
  >([]);
  const [appointments, setAppointments] = useState<
    { id: string; name: string }[]
  >([]);


  const fetchedRecordRef = useRef(false);

  const [data, setData] = useState<TreatmentRecordPayload>({
    id: "",
    patientId: "",
    clinicId: "",
    treatmentId: "",
    isAligner: false,
    upperJawOption: null,
    lowerJawOption: null,
    wireChange: null,
    treatmentNotes: "",
    appointmentId: null,
  });
  const [treatmentVisualFiles, setTreatmentVisualFiles] = useState<File[]>([]);
  const [existingTreatmentVisuals, setExistingTreatmentVisuals] = useState<
    string[]
  >([]);
  const [isVisualsModalOpen, setIsVisualsModalOpen] = useState(false);
  const [treatmentPlansLoading, setTreatmentPlansLoading] = useState(false);
  const [noPlanModalOpen, setNoPlanModalOpen] = useState(false);
  const [treatmentPlans, setTreatmentPlans] = useState<TreatmentPlan[]>([]);
  const [treatmentType, setTreatmentType] = useState<string | undefined>(undefined);
  const [isPreFilledFromTreatment, setIsPreFilledFromTreatment] = useState(false);

  const [errors, setErrors] = useState<{
    patientId?: string;
    clinicId?: string;
    appointmentId?: string;
    treatmentId?: string;
  }>({});

  const wireOptions = [
    "0.012 NiTi",
    "0.014 NiTi",
    "0.016 NiTi",
    "0.018 NiTi",
    "0.018 HANT",
    "0.016 × 0.022 NiTi",
    "0.016 × 0.022 SS",
    "0.017 × 0.025 NiTi",
    "0.017 × 0.025 HANT",
    "0.017 × 0.025 SS",
    "0.019 × 0.025 NiTi",
    "0.019 × 0.025 SS",
    "0.020 SS",
    "Others",
  ];


  const handleVisualFilesChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length === 0) return;

    setTreatmentVisualFiles((prev) => {
      const merged = [...prev, ...files];
      const seen = new Set<string>();
      const unique: File[] = [];
      for (const f of merged) {
        const key = `${f.name}-${f.size}-${f.lastModified}`;
        if (!seen.has(key)) {
          seen.add(key);
          unique.push(f);
        }
      }
      // Limit to maximum of 20 images to avoid oversize uploads
      if (unique.length > 20) {
        toast.warn("You can upload a maximum of 20 images. Extra files were ignored.");
      }
      return unique.slice(0, 20);
    });
    e.currentTarget.value = "";
  };

  useEffect(() => {
    if (!treatmentType) return;

    const isAligner = treatmentType === "ALIGNER";

    setData((prev) => ({
      ...prev,
      isAligner,

      // reset invalid fields automatically
      upperJawOption: isAligner ? null : prev.upperJawOption,
      lowerJawOption: isAligner ? null : prev.lowerJawOption,
      wireChange: isAligner ? prev.wireChange : null,
    }));
  }, [treatmentType]);


  useEffect(() => {
    const fetchClinics = async () => {
      setClinicsLoading(true);
      try {
        const res = await getClinicsByUser();
        const list = res?.data?.data || res?.data || [];
        const normalized = (Array.isArray(list) ? list : [])
          .map((c: any) => {
            const id = c.id || c._id || c.clinicId || c.value || "";
            const name = c.name || c.clinicName || c.label || "";
            return { id, name };
          })
          .filter((c) => c.id && c.name);
        setClinics(normalized);
      } catch {
        setClinics([]);
      } finally {
        setClinicsLoading(false);
      }
    };

    const fetchPatients = async () => {
      setPatientsLoading(true);
      try {
        const res = await getAllPatients();
        const list = res?.data?.data || res?.data || [];
        const normalized = (Array.isArray(list) ? list : [])
          .map((p: any) => {
            const id = p.id || p._id || p.patientId || p.value || "";
            const name =
              p.name ||
              `${p.firstName || ""} ${p.lastName || ""}`.trim() ||
              p.label ||
              "";
            const clinicId = p.clinicId || p.clinic?.id || "";
            const treatmentId = p.treatment_id || p.treatmentId || p.treatment?.id || "";
            return { id, name, clinicId, treatmentId };
          })
          .filter((p) => p.id && p.name && p.clinicId);
        setPatients(normalized);
      } catch {
        setPatients([]);
      } finally {
        setPatientsLoading(false);
      }
    };

    fetchClinics();
    fetchPatients();
  }, []);

  // Handle query parameters for pre-filling form when coming from treatment list
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const treatmentId = searchParams.get('treatmentId');
    const patientId = searchParams.get('patientId');
    const clinicId = searchParams.get('clinicId');

    if (treatmentId && patientId && clinicId && !id) {
      // Only pre-fill if we're creating a new record (not editing)
      setData(prev => ({
        ...prev,
        treatmentId,
        patientId,
        clinicId
      }));

      // Clear any validation errors since we're pre-filling valid data
      setErrors({});

      // Mark that form was pre-filled from treatment
      setIsPreFilledFromTreatment(true);
    }
  }, [location.search, id]);

  // Prefill when editing existing record
  useEffect(() => {
    if (!id || fetchedRecordRef.current) return;
    fetchedRecordRef.current = true;
    const fetchRecord = async () => {
      setLoading(true);
      try {
        const res = await getTreatmentRecordById(id as string);
        const root = res?.data?.data || res?.data || {};

        const recordData = root.data || root;

        const rawVisuals =
          recordData.treatmentVisuals ||
          root.treatmentVisuals ||
          [];

        const visuals = Array.isArray(rawVisuals)
          ? rawVisuals
            .map((v: any) =>
              typeof v === "string" ? v : v?.url || ""
            )
            .filter((v: string) => !!v)
          : [];

        setData({
          id: recordData.id || recordData._id || (id as string),

          patientId:
            recordData.patientId ||
            recordData.patient?.id ||
            recordData.patient ||
            "",

          clinicId:
            recordData.clinicId ||
            recordData.clinic?.id ||
            recordData.clinic ||
            "",

          appointmentId: recordData.appointmentId || null,

          treatmentId:
            recordData.treatmentId ||
            recordData.treatment?.id ||
            recordData.treatment ||
            "",

          // 🔥 NEW REQUIRED FIELDS
          isAligner: Boolean(recordData.isAligner),

          upperJawOption: recordData.isAligner
            ? null
            : recordData.upperJawOption ?? null,

          lowerJawOption: recordData.isAligner
            ? null
            : recordData.lowerJawOption ?? null,

          wireChange: recordData.isAligner
            ? recordData.wireChange || ""
            : null,

          treatmentNotes: recordData.treatmentNotes || "",

        });

        setExistingTreatmentVisuals(visuals);
      } catch (err: any) {
        toast.error(
          err?.response?.data?.message || "Failed to fetch treatment record"
        );
      } finally {
        setLoading(false);
      }
    };
    fetchRecord();
  }, [id]);

  useEffect(() => {
    const fetchAppointments = async () => {
      if (!data.patientId) {
        setAppointments([]);
        setData((prev) => ({ ...prev, appointmentId: null }));
        return;
      }

      try {
        setAppointmentsLoading(true);
        const res = await getPastAppointmentsByPatient(data.patientId);

        const list = res?.data?.data || res?.data || [];

        const normalized = Array.isArray(list)
          ? list.map((a: any) => ({
            id: a.id || a._id || a.appointmentId || "",
            name: a.name ||
              a.title ||
              `Appointment - ${a.appointmentTime
                ? `${formatTo12Hour(a.appointmentTime)} ${a.status || ""}`.trim()
                : `${a.status || ""}`.trim()
              }`,
          }))
          : [];

        setAppointments(normalized.filter((a) => a.id));
      } catch {
        setAppointments([]);
      } finally {
        setAppointmentsLoading(false);
      }
    };

    fetchAppointments();
  }, [data.patientId]);


  // When patient data is loaded and we have a patientId, check if patient has treatmentId
  useEffect(() => {
    if (data.patientId && patients.length > 0) {
      const selectedPatient = patients.find((p) => p.id === data.patientId);
      if (selectedPatient) {
        // If patient has treatmentId and it's not empty, use it
        const patientTreatmentId = selectedPatient.treatmentId;
        if (patientTreatmentId && patientTreatmentId.trim() !== "") {
          setData((prev) => ({
            ...prev,
            treatmentId: patientTreatmentId,
          }));
        } else if (!id) {
          // Only clear treatmentId for create mode if patient doesn't have one
          setData((prev) => ({ ...prev, treatmentId: "" }));
        }
      }
    }
  }, [data.patientId, patients, id]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;

    // Prevent changes when pre-filled from treatment
    if (
      isPreFilledFromTreatment &&
      (name === "clinicId" || name === "patientId")
    ) {
      return;
    }

    setData((prev) => {
      const next = {
        ...prev,
        [name]: value,
      };

      // 🔁 Clinic change → reset patient & treatment
      if (name === "clinicId" && value !== prev.clinicId) {
        next.patientId = "";
        next.treatmentId = "";
        next.appointmentId = null;
      }

      // 🔁 Patient change → reset appointment & auto-fill treatment
      if (name === "patientId") {
        const selectedPatient = patients.find((p) => p.id === value);

        next.appointmentId = null; // ✅ reset appointment

        if (
          selectedPatient?.treatmentId &&
          selectedPatient.treatmentId.trim() !== ""
        ) {
          next.treatmentId = selectedPatient.treatmentId;
        } else {
          next.treatmentId = "";
        }

        // Clear related errors
        setErrors((prevErrors) => ({
          ...prevErrors,
          patientId: undefined,
          appointmentId: undefined,
          treatmentId: undefined,
        }));
      }

      return next;
    });
  };

  // When patient changes, fetch treatment plans by patient and derive type
  useEffect(() => {
    const fetchPlans = async () => {
      if (!data.patientId) {
        setTreatmentPlans([]);
        setTreatmentType(undefined);
        return;
      }
      try {
        setTreatmentPlansLoading(true);
        const res = await getTreatmentPlansByPatient(data.patientId);
        const list = res?.data?.data || res?.data || [];
        const normalized: TreatmentPlan[] = Array.isArray(list) ? list : [];
        setTreatmentPlans(normalized);
        if (!normalized.length) {
          setTreatmentType(undefined);
          setNoPlanModalOpen(true);
        } else {
          const first = normalized[0];
          setTreatmentType(first?.type);
        }
      } catch {
        setTreatmentPlans([]);
        setTreatmentType(undefined);
        setNoPlanModalOpen(true);
      } finally {
        setTreatmentPlansLoading(false);
      }
    };
    fetchPlans();
  }, [data.patientId]);

  const validate = () => {
    const nextErrors: typeof errors = {};
    if (!data.patientId) nextErrors.patientId = "Patient is required";
    if (!data.clinicId) nextErrors.clinicId = "Clinic is required";
    // Check if selected patient has a treatmentId - show toast instead of form error
    if (data.patientId) {
      const selectedPatient = patients.find((p) => p.id === data.patientId);
      // Check if patient has treatmentId and it's not empty
      const patientTreatmentId = selectedPatient?.treatmentId;
      if (!patientTreatmentId || patientTreatmentId.trim() === "") {
        toast.error("Please create first treatment");
        return false;
      } else if (!data.treatmentId || data.treatmentId.trim() === "") {
        // If patient has treatmentId but data.treatmentId is not set, show toast
        toast.error("Please create first treatment");
        return false;
      }
    }
    if (!data.appointmentId) {
      nextErrors.appointmentId = "Appointment is required";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const filteredPatients =
    data.clinicId && patients.length
      ? patients.filter((p) => p.clinicId === data.clinicId)
      : [];
  const filteredAppointments = appointments;

  const getFullVisualUrl = (path: string) => getImageUrl(path);

  const handleRemoveVisualFile = (index: number) => {
    setTreatmentVisualFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const buildPayload = (): TreatmentRecordPayload => {
    if (data.isAligner) {
      return {
        ...data,
        isAligner: true,
        upperJawOption: null,
        lowerJawOption: null,
        wireChange: data.wireChange || "",
      };
    }

    return {
      ...data,
      isAligner: false,
      upperJawOption: data.upperJawOption || "",
      lowerJawOption: data.lowerJawOption || "",
      wireChange: null,
    };
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      // For create, do not send id; for edit, include it
      const finalPayload = buildPayload();

      const payloadData = id
        ? finalPayload
        : (({ id: _omit, ...rest }) => rest)(finalPayload);


      await createOrUpdateTreatmentRecord(
        payloadData as TreatmentRecordPayload,
        treatmentVisualFiles
      );
      toast.success("Treatment record saved successfully");
      navigate("/treatment-records");
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Failed to save treatment record"
      );
    } finally {
      setLoading(false);
    }
  };

  const formatTo12Hour = (time?: string) => {
    if (!time) return "";

    try {
      const [hours, minutes] = time.split(":").map(Number);

      const date = new Date();
      date.setHours(hours);
      date.setMinutes(minutes);

      return date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return time; // fallback
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <ToastContainer position="top-right" autoClose={3000} />
      {loading && <Loader />}
      {(clinicsLoading || patientsLoading || treatmentPlansLoading) && <Loader />}
      <Sidebar
        isOpen={isSidebarOpen}
        toggle={() => setIsSidebarOpen(!isSidebarOpen)}
      />
      <div className="flex-1 flex flex-col">
        <Topbar toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
        <main className="p-4 md:p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-6xl mx-auto"
          >
            <motion.div
              initial={{ scale: 0.98, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="w-full rounded-3xl shadow-2xl bg-white p-8"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                  {id ? "Edit Treatment Record" : "Create Treatment Record"}
                </h2>
                <button
                  className="px-4 py-1 rounded-xl bg-gray-100 text-gray-800 border border-gray-200 hover:bg-gray-200 transition flex items-center gap-2"
                  onClick={() => window.history.back()}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="w-4 h-4"
                    aria-hidden="true"
                  >
                    <path d="M10 19l-7-7 7-7" />
                    <path d="M3 12h18" />
                  </svg>
                  Back
                </button>
              </div>

              {/* Info message when pre-filled from treatment */}
              {isPreFilledFromTreatment && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center">
                    <Lock className="w-4 h-4 text-blue-600 mr-2" />
                    <p className="text-sm text-blue-800">
                      <strong>Pre-filled from Treatment:</strong> Clinic and Patient fields are locked and cannot be changed.
                      Please fill in the remaining fields to complete the treatment record.
                    </p>
                  </div>
                </div>
              )}

              <form
                noValidate
                onSubmit={handleSubmit}
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
              >

                {/* Clinic */}
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Clinic <span className="text-red-500">*</span>
                    {isPreFilledFromTreatment && (
                      <span className="ml-2 text-xs text-blue-600 font-normal inline-flex items-center">
                        <Lock className="w-3 h-3 mr-1" />
                        (Pre-filled from treatment)
                      </span>
                    )}
                  </label>
                  <select
                    name="clinicId"
                    value={data.clinicId}
                    onChange={handleChange}
                    required
                    disabled={clinicsLoading || isPreFilledFromTreatment}
                    className={`w-full mt-1 p-2 border rounded-lg ${isPreFilledFromTreatment
                      ? "bg-gray-100 cursor-not-allowed"
                      : ""
                      } ${errors.clinicId
                        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                        : ""
                      }`}
                  >
                    {clinicsLoading ? (
                      <option value="">Loading clinics...</option>
                    ) : (
                      <>
                        <option value="">Select Clinic</option>
                        {clinics.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                  {errors.clinicId && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.clinicId}
                    </p>
                  )}
                </div>

                {/* Patient (filtered by selected clinic) */}
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Patient <span className="text-red-500">*</span>
                    {isPreFilledFromTreatment && (
                      <span className="ml-2 text-xs text-blue-600 font-normal inline-flex items-center">
                        <Lock className="w-3 h-3 mr-1" />
                        (Pre-filled from treatment)
                      </span>
                    )}
                  </label>
                  <select
                    name="patientId"
                    value={data.patientId}
                    onChange={handleChange}
                    required
                    disabled={patientsLoading || !data.clinicId || isPreFilledFromTreatment}
                    className={`w-full mt-1 p-2 border rounded-lg ${isPreFilledFromTreatment
                      ? "bg-gray-100 cursor-not-allowed"
                      : ""
                      } ${errors.patientId
                        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                        : ""
                      }`}
                  >
                    {patientsLoading ? (
                      <option value="">Loading patients...</option>
                    ) : !data.clinicId ? (
                      <option value="">Select clinic first</option>
                    ) : filteredPatients.length === 0 ? (
                      <option value="" disabled>
                        Please add patient in clinic..
                      </option>
                    ) : (
                      <>
                        <option value="">Select Patient</option>
                        {filteredPatients.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                  {errors.patientId && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.patientId}
                    </p>
                  )}
                </div>

                {/* Appointments (filtered by selected Patient) */}
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Appointments <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="appointmentId"
                    value={data.appointmentId ?? ""}
                    onChange={handleChange}
                    required
                    disabled={appointmentsLoading || !data.patientId}
                    className={`w-full flex flex-wrap gap-2 items-center justify-between border rounded-xl px-3 py-2 text-left ${data.appointmentId ? "bg-gray-50 cursor-not-allowed" : ""
                      }`}
                  >
                    {appointmentsLoading ? (
                      <option value="">Loading Appointments...</option>
                    ) : !data.patientId ? (
                      <option value="">Select patient first</option>
                    ) : filteredAppointments.length === 0 ? (
                      <option value="" disabled>
                        Please add Appointment for patient..
                      </option>
                    ) : (
                      <>
                        <option value="">Select Appointment</option>
                        {filteredAppointments.map((A) => (
                          <option key={A.id} value={A.id}>
                            {A.name}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                  {errors.appointmentId && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.appointmentId}
                    </p>
                  )}
                </div>


                {/* Wire / Aligner Change (label + control depend on treatment type) */}
                {/* ALIGNER → Input */}
                {data.isAligner && (
                  <div>
                    <label className="block text-sm font-medium text-gray-600">
                      Aligner Change
                    </label>
                    <input
                      name="wireChange"
                      value={data.wireChange ?? ""}
                      onChange={handleChange}
                      className="mt-1 w-full p-2 border rounded-xl"
                      placeholder="Enter aligner change"
                    />
                  </div>
                )}

                {/* BRACES → Upper & Lower Jaw Dropdowns */}
                {!data.isAligner && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-600">
                        Upper Jaw Option
                      </label>
                      <select
                        name="upperJawOption"
                        value={data.upperJawOption ?? ""}
                        onChange={handleChange}
                        className="mt-1 w-full p-2 border rounded-xl"
                      >
                        <option value="">Select option</option>
                        {wireOptions.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-600">
                        Lower Jaw Option
                      </label>
                      <select
                        name="lowerJawOption"
                        value={data.lowerJawOption ?? ""}
                        onChange={handleChange}
                        className="mt-1 w-full p-2 border rounded-xl"
                      >
                        <option value="">Select option</option>
                        {wireOptions.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}


                {/* Treatment Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Treatment Notes
                  </label>
                  <textarea
                    name="treatmentNotes"
                    value={data.treatmentNotes}
                    onChange={handleChange}
                    rows={3}
                    className="mt-1 w-full p-2 border rounded-xl resize-none"
                    placeholder="Enter any treatment notes"
                  />
                </div>

                {/* Treatment Visuals */}
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Treatment Visuals (files)
                  </label>
                  <div className="mt-1 flex flex-col gap-2">
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleVisualFilesChange}
                      className="block w-full text-sm text-gray-700 file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:bg-blue-600 file:text-white hover:file:bg-blue-700 cursor-pointer"
                    />
                    <div className="flex items-center justify-between text-xs text-gray-600">
                      <div>
                        {existingTreatmentVisuals.length > 0 && (
                          <span className="mr-3">
                            Existing: {existingTreatmentVisuals.length}
                          </span>
                        )}
                        {treatmentVisualFiles.length > 0 && (
                          <span>
                            Selected: {treatmentVisualFiles.length}
                          </span>
                        )}
                      </div>
                      {(existingTreatmentVisuals.length > 0 ||
                        treatmentVisualFiles.length > 0) && (
                          <button
                            type="button"
                            onClick={() => setIsVisualsModalOpen(true)}
                            className="px-2 py-1 rounded-lg bg-gray-100 border border-gray-200 text-gray-800 hover:bg-gray-200 transition text-[11px]"
                          >
                            View files
                          </button>
                        )}
                    </div>
                  </div>
                </div>

                {isVisualsModalOpen && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div
                      className="absolute inset-0 bg-black/40"
                      onClick={() => setIsVisualsModalOpen(false)}
                    />
                    <div className="relative z-10 w-full max-w-3xl bg-white rounded-2xl shadow-2xl p-5 max-h-[80vh] overflow-y-auto">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-lg font-semibold text-gray-800">
                          Treatment Visuals
                        </h3>
                        <button
                          type="button"
                          onClick={() => setIsVisualsModalOpen(false)}
                          className="text-gray-500 hover:text-gray-700"
                          aria-label="Close"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="w-5 h-5"
                          >
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        </button>
                      </div>

                      {existingTreatmentVisuals.length > 0 && (
                        <div className="mb-4">
                          <div className="text-xs font-medium text-gray-500 mb-2">
                            Existing
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {existingTreatmentVisuals.map((v, idx) => {
                              const src = getFullVisualUrl(v);
                              return (
                                <div
                                  key={`existing-${idx}`}
                                  className="relative border border-gray-200 rounded-lg overflow-hidden bg-gray-50"
                                >
                                  <img
                                    src={src}
                                    alt={`Visual ${idx + 1}`}
                                    className="w-full h-32 object-cover"
                                  />

                                  <div className="px-2 py-1 text-[11px] text-gray-600 truncate">
                                    {src}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {treatmentVisualFiles.length > 0 && (
                        <div>
                          <div className="text-xs font-medium text-gray-500 mb-2">
                            Selected to upload ({treatmentVisualFiles.length})
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {treatmentVisualFiles.map((file, index) => (
                              <div
                                key={`${file.name}-${index}`}
                                className="relative border border-gray-200 rounded-lg overflow-hidden bg-gray-50"
                              >
                                <img
                                  src={URL.createObjectURL(file)}
                                  alt={file.name}
                                  className="w-full h-32 object-cover"
                                />
                                <button
                                  type="button"
                                  onClick={() => handleRemoveVisualFile(index)}
                                  className="absolute top-1 right-1 bg-white/80 hover:bg-red-600 hover:text-white text-xs px-1.5 py-0.5 rounded"
                                >
                                  Remove
                                </button>
                                <div className="px-2 py-1 text-[11px] text-gray-600 truncate">
                                  {file.name}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="md:col-span-2 flex justify-end gap-3 mt-4">
                  <button
                    type="button"
                    className="px-5 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                    onClick={() => window.history.back()}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-5 py-2 rounded-xl text-white transition disabled:opacity-60"
                  >
                    {loading ? "Saving..." : "Save Treatment Record"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        </main>
      </div>

      {/* Modal shown when no treatment plan exists for selected patient */}
      {noPlanModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-2 text-red-600">
              No Treatment Plan Found
            </h3>
            <p className="text-gray-600 text-sm mb-6">
              No treatment plans found for this patient. Please create first treatment plan.
            </p>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setNoPlanModalOpen(false);
                  navigate("/treatment-records");
                }}
                className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TreatmentRecordForm;


